import React, { useEffect, useRef } from "react";
import { Modal, Button, Typography } from "antd";
import { RightOutlined, LeftOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import { useRouteLoaderData } from "react-router-dom";
import { useAppGuide } from "./AppGuideProvider";
import { readAppGuideDismissed } from "./appGuideSession";
import {
  JOURNEY_SECTIONS,
  TOTAL_JOURNEY_SECTIONS,
} from "./sections/journeySections";
import { JourneySectionFrame } from "./sections/JourneySectionFrame";
import type { MainLayoutLoaderData } from "~/components/Layout/MainLayout/MainLayout";
import {
  spacing,
  colors,
  layout,
  mediaQueries,
  fontSize,
} from "~/styles/tokens";
import "./journeyGuide.css";

const { Text } = Typography;

/** @deprecated Use TOTAL_JOURNEY_SECTIONS — kept for MainLayout import stability */
export const TOTAL_SECTIONS = TOTAL_JOURNEY_SECTIONS;

/**
 * Journey Guide modal — Explore-inspired shell with progress rail and Show me.
 */
export const AppGuideModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    isOpen,
    currentSection,
    closeGuide,
    goToSection,
    nextSection,
    prevSection,
    markAsSeen,
    openGuide,
  } = useAppGuide();

  const isMobile = useMediaQuery(mediaQueries.mobile);
  const mainData = useRouteLoaderData("main") as
    | MainLayoutLoaderData
    | undefined;
  const didAutoOpen = useRef(false);

  // First authenticated visit this session — open once. Read sessionStorage
  // here (client-only) so a reload cannot ignore a prior Finish/close.
  useEffect(() => {
    if (didAutoOpen.current) {
      return;
    }
    if (!mainData?.isAuthenticated || isOpen) {
      return;
    }
    if (readAppGuideDismissed()) {
      return;
    }
    didAutoOpen.current = true;
    const timer = setTimeout(() => {
      // Persist before showing so a later reload does not auto-open again.
      markAsSeen();
      openGuide(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [mainData?.isAuthenticated, isOpen, openGuide, markAsSeen]);

  const handleClose = () => {
    closeGuide();
  };

  const section = JOURNEY_SECTIONS[currentSection];
  const CurrentSectionComponent = section?.component;
  const isLast = currentSection === JOURNEY_SECTIONS.length - 1;

  return (
    <Modal
      className="journey-guide-modal"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={isMobile ? layout.modalFullscreen : layout.modalStandard}
      closeIcon={<CloseOutlined />}
      styles={{
        body: { padding: "0" },
      }}
      style={
        isMobile
          ? { top: 20, maxWidth: "100%", margin: `0 ${spacing.inline}` }
          : undefined
      }
    >
      <div
        className={`journey-guide${isMobile ? " is-mobile" : ""}`}
        style={
          {
            "--journey-primary": colors.actionPrimary,
            "--journey-surface-subtle": colors.surfaceSubtle,
            "--journey-border": colors.borderSubtle,
            "--journey-text": "#262626",
            "--journey-text-secondary": colors.textSecondary,
            "--journey-success": colors.statusSuccess,
          } as React.CSSProperties
        }
      >
        {isMobile ? (
          <div className="journey-mobile-rail">
            <div className="journey-mobile-dots">
              {JOURNEY_SECTIONS.map((item, index) => (
                <button
                  key={item.key}
                  type="button"
                  className={`journey-mobile-dot${
                    index === currentSection ? " is-active" : ""
                  }${index < currentSection ? " is-complete" : ""}`}
                  onClick={() => goToSection(index)}
                  aria-label={t(item.titleKey)}
                  aria-current={index === currentSection ? "step" : undefined}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            {section && (
              <div className="journey-mobile-label">{t(section.titleKey)}</div>
            )}
          </div>
        ) : (
          <nav className="journey-rail" aria-label={t("JourneyGuide_Sections")}>
            <div className="journey-rail-label">{t("JourneyGuide_Sections")}</div>
            <ol className="journey-rail-steps">
              {JOURNEY_SECTIONS.map((item, index) => (
                <li key={item.key}>
                  <button
                    type="button"
                    className={`journey-rail-step${
                      index === currentSection ? " is-active" : ""
                    }${index < currentSection ? " is-complete" : ""}`}
                    onClick={() => goToSection(index)}
                  >
                    <span className="journey-rail-index">{index + 1}</span>
                    <span className="journey-rail-title">
                      {t(item.titleKey)}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="journey-main">
          {section && CurrentSectionComponent && (
            <JourneySectionFrame
              sectionKey={section.key}
              titleKey={section.titleKey}
              showMePath={section.showMePath}
              isMobile={isMobile}
            >
              <CurrentSectionComponent isMobile={isMobile} />
            </JourneySectionFrame>
          )}
        </div>

        <div className="journey-footer">
          <Button
            icon={<LeftOutlined />}
            onClick={prevSection}
            disabled={currentSection === 0}
            size={isMobile ? "small" : "middle"}
          >
            {!isMobile && t("AppGuide_Previous")}
          </Button>

          <Text
            type="secondary"
            style={{ fontSize: isMobile ? fontSize.caption : fontSize.body }}
          >
            {currentSection + 1} / {JOURNEY_SECTIONS.length}
          </Text>

          {isLast ? (
            <Button
              type="primary"
              onClick={handleClose}
              size={isMobile ? "small" : "middle"}
            >
              {t("AppGuide_Finish")}
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={nextSection}
              size={isMobile ? "small" : "middle"}
            >
              {!isMobile && t("AppGuide_Next")} <RightOutlined />
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
